import { TrendingUp, AlertTriangle } from "lucide-react";
import clsx from "clsx";
import { computeStats, type TradeForStats } from "@/lib/stats";
import { formatPrice, formatPercent } from "@/lib/format";
import type { TradeRow } from "@/hooks/useTrades";

interface StatsPanelProps {
  trades: TradeRow[];
  accountBalance: number;
}

export function StatsPanel({ trades, accountBalance }: StatsPanelProps) {
  const stats = computeStats(trades as TradeForStats[], accountBalance);

  if (stats.total === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-bg-elevated p-3 text-center text-2xs text-text-muted">
        Close some trades to see stats.
      </div>
    );
  }

  const profitable = stats.netPnl >= 0;

  return (
    <div className="rounded-md border border-border bg-bg-elevated p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wider text-text-muted">
          <TrendingUp className="h-3 w-3" />
          <span>Backtest stats</span>
        </div>
        <div className={clsx("font-mono text-sm font-bold tabular", profitable ? "text-bull" : "text-bear")}>
          {profitable ? "+" : ""}${formatPrice(stats.netPnl)}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-2xs">
        <Cell label="Trades" value={stats.total.toString()} />
        <Cell label="Win rate" value={formatPercent(stats.winRate, 1)} tone={stats.winRate >= 50 ? "bull" : "bear"} />

        <Cell label="Wins / Losses" value={`${stats.wins} / ${stats.losses}`} />
        <Cell
          label="Profit factor"
          value={Number.isFinite(stats.profitFactor) ? stats.profitFactor.toFixed(2) : "∞"}
          tone={stats.profitFactor >= 1.5 ? "bull" : stats.profitFactor < 1 ? "bear" : undefined}
        />

        <Cell label="Avg win" value={`$${formatPrice(stats.avgWin)}`} tone="bull" />
        <Cell label="Avg loss" value={`$${formatPrice(stats.avgLoss)}`} tone="bear" />

        <Cell label="Largest win" value={`$${formatPrice(stats.largestWin)}`} tone="bull" />
        <Cell label="Largest loss" value={`$${formatPrice(stats.largestLoss)}`} tone="bear" />

        <Cell label="Expectancy" value={`$${formatPrice(stats.expectancy)}`} />
        <Cell
          label="Max DD"
          value={`$${formatPrice(stats.maxDrawdown)}`}
          subValue={`${stats.maxDrawdownPct.toFixed(1)}%`}
          tone="bear"
          warn={stats.maxDrawdownPct > 25}
        />
      </div>
    </div>
  );
}

function Cell({
  label,
  value,
  subValue,
  tone,
  warn,
}: {
  label: string;
  value: string;
  subValue?: string;
  tone?: "bull" | "bear";
  warn?: boolean;
}) {
  const toneClass =
    tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : "text-text-primary";

  return (
    <div className="flex flex-col">
      <span className="text-text-muted">{label}</span>
      <span className={clsx("font-mono tabular flex items-center gap-1", toneClass)}>
        {value}
        {subValue && <span className="text-text-muted opacity-70 text-[10px]">{subValue}</span>}
        {warn && <AlertTriangle className="h-3 w-3 text-warn" aria-label="Significant drawdown" />}
      </span>
    </div>
  );
}
